import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock3,
  FileCheck2,
  FileText,
  HelpCircle,
  ListChecks,
  LoaderCircle,
  LockKeyhole,
  RefreshCw,
  Scale,
  ShieldCheck,
  Sparkles,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ConfirmDialog, useToast } from "@tribunalea/ui";
import { CivitaAssistantLauncher } from "../components/CivitaAssistant.jsx";
import { DocumentPreview } from "../components/DocumentPreview.jsx";
const pressureOptions = Object.freeze([
  { id: "none", label: "Sin apuro", description: "Puedo revisarlo con tiempo" },
  { id: "soon", label: "Pronto", description: "Me piden una respuesta en estos días" },
  { id: "today", label: "Hoy", description: "Esperan que lo firme ahora" },
]);
const termCategoryLabels = {
  cost: "Costo",
  duration: "Duración",
  exit: "Salida",
  other: "Dato clave",
};

const signingDemoLines = [
  "ACUERDO DE SERVICIO — PLAN ANUAL",
  "Fecha: 12 de septiembre de 2026",
  "",
  "El cliente contrata el Plan Anual por un valor total de $360.000,",
  "pagadero en 12 cuotas mensuales de $30.000.",
  "",
  "El servicio se renovará automáticamente por períodos de doce meses,",
  "salvo aviso escrito con treinta (30) días de anticipación.",
  "",
  "La bonificación ofrecida durante los primeros tres meses no modifica",
  "el valor total indicado en este acuerdo.",
  "",
  "La solicitud de baja no extingue las cuotas pendientes del período contratado.",
];

const demoReview = Object.freeze({
  id: "revision-ejemplo",
  notebookId: "ejemplo",
  purpose: "Contratar un servicio con precio promocional por tres meses.",
  promised: "Me dijeron que podía darlo de baja cuando quisiera sin pagar nada más.",
  concern: "No sé si después de los tres meses cambia el precio ni cuánto dura.",
  pressure: "soon",
  demo: true,
  sources: [
    { id: "acuerdo", fileName: "acuerdo-de-servicio.pdf", status: "ready", pageCount: 1 },
  ],
  analysisStatus: "ready",
  status: "ready",
  createdAt: new Date().toISOString(),
  analysis: {
    title: "Acuerdo de servicio con duración anual",
    documentType: "Contrato de prestación de servicios",
    summary: "Es un plan anual de $360.000 con renovación automática.",
    decisionFrame: "Antes de decidir, conviene confirmar por escrito qué sucede si pedís la baja durante el primer año y cómo se aplica la promoción mencionada.",
    pressureNote: "Pedí una copia completa y las aclaraciones por escrito antes de firmar.",
    priorities: [
      {
        tone: "danger",
        title: "La baja no coincide con lo que te dijeron",
        explanation: "El documento mantiene las cuotas pendientes del período anual.",
        action: "Pedí por escrito el importe exacto si cancelás antes del mes doce.",
        citations: [{ fileName: "acuerdo-de-servicio.pdf", page: 1, quote: "La solicitud de baja no extingue las cuotas pendientes del período contratado." }],
      },
      {
        tone: "warning",
        title: "La bonificación no está detallada",
        explanation: "No aparece su importe ni el precio efectivo de las primeras cuotas.",
        action: "Pedí el cronograma de las doce cuotas con la promoción aplicada.",
        citations: [{ fileName: "acuerdo-de-servicio.pdf", page: 1, quote: "La bonificación ofrecida durante los primeros tres meses no modifica" }],
      },
      {
        tone: "warning",
        title: "No se detallan las prestaciones",
        explanation: "Esta página no explica qué incluye exactamente el Plan Anual.",
        action: "Solicitá el anexo o listado de servicios antes de aceptar.",
        citations: [],
      },
    ],
    keyTerms: [
      { category: "cost", label: "Valor total", value: "$360.000", citations: [{ fileName: "acuerdo-de-servicio.pdf", page: 1, quote: "por un valor total de $360.000" }] },
      { category: "duration", label: "Duración", value: "12 meses", citations: [{ fileName: "acuerdo-de-servicio.pdf", page: 1, quote: "PLAN ANUAL" }] },
      { category: "exit", label: "Aviso para no renovar", value: "30 días antes", citations: [{ fileName: "acuerdo-de-servicio.pdf", page: 1, quote: "salvo aviso escrito con treinta (30) días de anticipación." }] },
    ],
    nextActions: [
      { type: "question", text: "Si pido la baja en el cuarto mes, ¿qué importe exacto queda pendiente?" },
      { type: "question", text: "¿Cuál es el precio de cada cuota con la promoción aplicada?" },
      { type: "change", text: "Agregar el costo exacto de la baja anticipada al contrato." },
    ],
    needsProfessionalHelp: false,
    professionalHelpReason: null,
  },
});

function formatDate(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat("es-AR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}

function formatSize(bytes) {
  if (!Number.isFinite(bytes)) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function StatusStep({ state, children }) {
  return (
    <li className={`processing-step is-${state}`}>
      <span>
        {state === "done" ? <Check size={15} /> : state === "active" ? <LoaderCircle className="is-spinning" size={15} /> : null}
      </span>
      {children}
    </li>
  );
}

function SigningProcessing({ review, onBack }) {
  const extracted = review.sources?.length > 0
    && review.sources.every(({ status }) => status === "ready");
  return (
    <main className="civita-document-page signing-page processing-view">
      <button className="text-button" type="button" onClick={onBack}><ArrowLeft size={16} /> Volver</button>
      <section className="processing-card" aria-live="polite">
        <span className="processing-card__icon"><Sparkles size={25} /></span>
        <span className="eyebrow">Revisar antes de firmar</span>
        <h1>Estamos leyendo lo que aceptarías</h1>
        <p>Revisamos el documento y sus anexos juntos, sin completar lo que no esté escrito.</p>
        <ol>
          <StatusStep state="done">Archivos recibidos de forma segura</StatusStep>
          <StatusStep state={extracted ? "done" : "active"}>Leyendo texto, imágenes y páginas</StatusStep>
          <StatusStep state={extracted ? "active" : "pending"}>Separando compromisos, costos, plazos y puntos a aclarar</StatusStep>
        </ol>
        <small>Esto suele demorar menos de un minuto. Al terminar, la pantalla quedará arriba del análisis.</small>
      </section>
    </main>
  );
}

function SigningError({ review, retrying, onRetry, onBack }) {
  return (
    <main className="civita-document-page signing-page processing-view">
      <section className="processing-card is-error">
        <span className="processing-card__icon"><AlertCircle size={25} /></span>
        <span className="eyebrow">No pudimos terminar</span>
        <h1>Necesitamos intentar otra vez</h1>
        <p>{review.error?.message || "Ocurrió un problema al leer o revisar los documentos."}</p>
        <div className="button-row">
          <button className="primary-button" type="button" onClick={onRetry} disabled={retrying}>
            {retrying ? <LoaderCircle className="is-spinning" size={17} /> : <RefreshCw size={17} />} Reintentar
          </button>
          <button className="secondary-button" type="button" onClick={onBack}>Empezar otra revisión</button>
        </div>
      </section>
    </main>
  );
}

function CitationButton({ citation, label = "Ver cita", onSelect }) {
  if (!citation?.page || !citation?.quote) return null;
  return (
    <button className="citation-button" type="button" onClick={() => onSelect(citation)} title={`${citation.fileName}: ${citation.quote}`}>
      {label} · pág. {citation.page} <ChevronRight size={13} />
    </button>
  );
}

function Citations({ citations, onSelect }) {
  if (!citations?.length) return null;
  const multiple = citations.length > 1;
  return <div className="signing-citations">{citations.map((citation, index) => <CitationButton key={`${citation.fileName}-${citation.page}-${index}`} citation={citation} label={multiple ? `Cita ${index + 1}` : "Ver cita"} onSelect={onSelect} />)}</div>;
}

function SigningResult({ api, review, onBack, onDelete }) {
  const parameters = useMemo(() => new URLSearchParams(window.location.search), []);
  const requestedSource = parameters.get("source");
  const requestedPage = Number(parameters.get("page"));
  const [sourceId, setSourceId] = useState(() => (
    review.sources.find(({ id }) => id === requestedSource)?.id || review.sources[0]?.id
  ));
  const [page, setPage] = useState(() => (
    Number.isInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1
  ));
  const fullAnalysisRef = useRef(null);
  const previewRef = useRef(null);
  const analysis = review.analysis;
  const source = review.sources.find(({ id }) => id === sourceId) || review.sources[0];
  const previewApi = useMemo(() => ({
    getDocumentBlob: () => api.getSigningReviewSourceBlob(review.id, source?.id),
  }), [api, review.id, source?.id]);
  const previewDocument = useMemo(() => ({
    ...source,
    demo: review.demo,
  }), [review.demo, source]);
  const conversationMetadata = useMemo(() => ({
    civitaSigningReviewId: review.id,
    notebookId: review.notebookId,
  }), [review.id, review.notebookId]);
  const priorities = analysis.priorities || [];
  const practicalTerms = analysis.keyTerms || [];
  const nextActions = analysis.nextActions || [];

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [review.id]);

  function selectCitation(citation) {
    const matchingSource = review.sources.find(({ fileName }) => fileName === citation.fileName);
    if (matchingSource) setSourceId(matchingSource.id);
    setPage(citation.page || 1);
    if (fullAnalysisRef.current) fullAnalysisRef.current.open = true;
    window.setTimeout(() => previewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  }

  return (
    <main className="civita-document-page signing-page signing-result-page">
      {review.demo && <div className="demo-banner"><Sparkles size={16} /> Este es un ejemplo ilustrativo. Ningún archivo fue enviado ni analizado.</div>}
      <header className="result-header">
        <button className="text-button" type="button" onClick={onBack}><ArrowLeft size={16} /> Mis revisiones</button>
        <div className="result-header__actions">
          <span><FileText size={15} /> {review.sources.length} {review.sources.length === 1 ? "archivo" : "archivos"}</span>
          {!review.demo && <button className="danger-icon-button" type="button" onClick={onDelete} title="Eliminar revisión"><Trash2 size={17} /></button>}
        </div>
      </header>

      <section className="signing-result-heading signing-result-heading--compact">
        <div>
          <span className="eyebrow">{analysis.documentType}</span>
          <h1>{analysis.title}</h1>
          <p>{analysis.summary}</p>
        </div>
        <span className="analysis-ready"><CheckCircle2 size={16} /> Revisión lista</span>
      </section>

      <section className="signing-decision-frame signing-decision-frame--primary">
        <span className="signing-decision-frame__icon"><Scale size={21} /></span>
        <div>
          <span className="eyebrow">Lo principal</span>
          <p>{analysis.decisionFrame}</p>
        </div>
      </section>

      <div className="signing-brief-grid">
        <section className="signing-brief-card signing-priority-summary">
          <header>
            <div><span className="eyebrow">Antes de firmar</span><h2>{priorities.length ? `${priorities.length} ${priorities.length === 1 ? "punto para resolver" : "puntos para resolver"}` : "No encontramos alertas principales"}</h2></div>
            {priorities.length > 0 && <span className="signing-brief-count">{priorities.length}</span>}
          </header>
          {priorities.length ? (
            <div className="signing-priority-list">
              {priorities.map((item, index) => (
                <article className={`is-${item.tone}`} key={`${item.title}-${index}`}>
                  <span>{item.tone === "danger" ? <AlertCircle size={18} /> : <HelpCircle size={18} />}</span>
                  <div><h3>{item.title}</h3><p>{item.explanation}</p><strong>{item.action}</strong><Citations citations={item.citations} onSelect={selectCitation} /></div>
                </article>
              ))}
            </div>
          ) : <p className="signing-brief-empty"><CheckCircle2 size={18} /> Aun así, comprobá los importes, las fechas y cómo pedir la baja.</p>}
        </section>

        <aside className="signing-brief-card signing-practical-summary">
          <header><span className="eyebrow">En breve</span><h2>Lo que aceptarías</h2></header>
          <dl>
            {practicalTerms.map((item, index) => <div key={`${item.category}-${item.label}-${index}`}><dt>{termCategoryLabels[item.category] || "Dato clave"}</dt><dd><strong>{item.value}</strong><span>{item.label}</span><Citations citations={item.citations} onSelect={selectCitation} /></dd></div>)}
          </dl>
        </aside>
      </div>

      {analysis.pressureNote && (
        <section className="signing-pressure-note"><Clock3 size={19} /><div><strong>Si te apuran para firmar</strong><p>{analysis.pressureNote}</p></div></section>
      )}

      {nextActions.length > 0 && (
        <section className="signing-next-actions">
          <header><span className="section-icon"><ListChecks size={19} /></span><div><span className="eyebrow">Próximo paso</span><h2>Qué podés hacer ahora</h2></div></header>
          <ol>{nextActions.map((item, index) => <li key={`${item.text}-${index}`}><span>{index + 1}</span><div><small>{item.type === "change" ? "Pedí por escrito" : "Preguntá"}</small><strong>{item.text}</strong></div></li>)}</ol>
        </section>
      )}

      {analysis.needsProfessionalHelp && (
        <section className="professional-note signing-professional-note"><ShieldCheck size={21} /><div><h2>Puede ser útil revisarlo con un profesional antes de decidir</h2><p>{analysis.professionalHelpReason}</p></div></section>
      )}

      <details className="signing-full-report" ref={fullAnalysisRef}>
        <summary><span><FileText size={18} /><span><strong>Ver el documento original</strong><small>Comprobá las citas y leé el texto completo.</small></span></span><ChevronDown size={19} /></summary>
        <div className="signing-document-report" ref={previewRef}>
          {review.sources.length > 1 && (
            <label className="signing-source-picker"><span>Archivo que estás viendo</span><select value={source?.id || ""} onChange={(event) => { setSourceId(event.target.value); setPage(1); }}>{review.sources.map((item) => <option value={item.id} key={item.id}>{item.fileName}</option>)}</select></label>
          )}
          <DocumentPreview api={previewApi} document={previewDocument} page={page} onPageChange={setPage} label="Texto original" demoContent={signingDemoLines} />
          <p className="signing-preview-note"><LockKeyhole size={13} /> Las explicaciones se basan únicamente en estos archivos y en el contexto que ingresaste.</p>
        </div>
      </details>

      <p className="ai-disclaimer">Esta revisión fue generada con IA y puede equivocarse. Verificá los puntos importantes en el documento original. Civita no decide por vos si conviene firmar.</p>

      <CivitaAssistantLauncher
        className="signing-assistant"
        title="Seguí revisando antes de decidir"
        description="Justiniano se abrirá sobre esta pantalla usando los documentos y el contexto que contaste."
        sourceLabel="Solo esta revisión"
        conversationScope={`revision-firma:${review.id}:v1`}
        conversationMetadata={conversationMetadata}
        notebookIds={[review.notebookId]}
        requestedSources={["library"]}
        welcome="Ya revisamos los documentos completos. Puedo ayudarte a comparar una explicación con lo escrito, entender una condición o preparar una pregunta concreta antes de que decidas."
        suggestions={["¿Qué tendría que pagar si quiero salir?", "¿Qué promesa no quedó escrita?", "Ayudame a redactar las preguntas para la otra parte"]}
        appContext={{
          route: "/revisar-antes-de-firmar",
          activeEntity: { type: "signing_review", id: review.id, label: analysis.title },
          signingReview: {
            purpose: review.purpose,
            promised: review.promised,
            concern: review.concern,
            pressure: review.pressure,
            analysis,
          },
        }}
      />
    </main>
  );
}

export function ReviewBeforeSigningPage({ api, authEnabled }) {
  const { showToast } = useToast();
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [purpose, setPurpose] = useState("");
  const [promised, setPromised] = useState("");
  const [concern, setConcern] = useState("");
  const [pressure, setPressure] = useState("none");
  const [dragging, setDragging] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(authEnabled);
  const [submitting, setSubmitting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  function selectReview(review) {
    setSelected(review);
    if (!review?.demo) window.history.replaceState({}, "", `/revisar-antes-de-firmar?review=${encodeURIComponent(review.id)}`);
  }

  function resetView() {
    setSelected(null);
    setFiles([]);
    setPurpose("");
    setPromised("");
    setConcern("");
    setPressure("none");
    window.history.replaceState({}, "", "/revisar-antes-de-firmar");
  }

  useEffect(() => {
    if (!authEnabled) return undefined;
    let active = true;
    api.listSigningReviews().then((items) => {
      if (!active) return;
      setReviews(items);
      const parameters = new URLSearchParams(window.location.search);
      const requestedId = parameters.get("review");
      const notebook = parameters.get("notebook");
      const source = parameters.get("source");
      const match = items.find((item) => (requestedId && item.id === requestedId) || (notebook && item.notebookId === notebook) || (source && item.sources.some(({ id }) => id === source)));
      if (match) setSelected(match);
    }).catch((error) => showToast({ message: error.message, tone: "danger" })).finally(() => active && setLoadingHistory(false));
    return () => { active = false; };
  }, [api, authEnabled, showToast]);

  useEffect(() => {
    if (!selected || selected.demo || selected.status === "ready" || selected.status === "failed") return undefined;
    let active = true;
    let timer;
    async function refresh() {
      try {
        let current = await api.getSigningReview(selected.id);
        const sourcesReady = current.sources?.length > 0 && current.sources.every(({ status }) => status === "ready");
        if (sourcesReady && ["waiting", "analyzing"].includes(current.analysisStatus)) current = await api.analyzeSigningReview(current.id);
        if (!active) return;
        setSelected(current);
        setReviews((items) => items.map((item) => item.id === current.id ? current : item));
        if (!['ready', 'failed'].includes(current.status)) timer = window.setTimeout(refresh, 1_800);
      } catch (error) {
        if (!active) return;
        if (error.code === "civita_signing_review_not_ready") { timer = window.setTimeout(refresh, 1_800); return; }
        setSelected((current) => current ? { ...current, status: "failed", error: { message: error.message, code: error.code } } : current);
      }
    }
    void refresh();
    return () => { active = false; window.clearTimeout(timer); };
  }, [api, selected?.demo, selected?.id, selected?.status]);

  function chooseFiles(fileList) {
    const next = [...(fileList || [])];
    if (!next.length) return;
    if (next.length > 10) { showToast({ message: "Podés subir hasta 10 archivos.", tone: "danger" }); return; }
    const invalid = next.some((file) => !["application/pdf", "image/jpeg", "image/png", "image/webp"].includes(file.type) && !/\.(?:pdf|jpe?g|png|webp)$/i.test(file.name));
    if (invalid) { showToast({ message: "Usá archivos PDF, JPG, PNG o WEBP.", tone: "danger" }); return; }
    if (next.reduce((total, file) => total + file.size, 0) > 20 * 1024 * 1024) { showToast({ message: "Los archivos superan el límite total de 20 MB.", tone: "danger" }); return; }
    setFiles(next);
  }

  async function submit(event) {
    event.preventDefault();
    if (!files.length || purpose.trim().length < 5 || submitting) return;
    if (!authEnabled) { selectReview(demoReview); return; }
    setSubmitting(true);
    try {
      const created = await api.createSigningReview({ files, purpose, promised, concern, pressure });
      setReviews((items) => [created, ...items]);
      selectReview(created);
    } catch (error) {
      showToast({ message: error.message, tone: "danger" });
    } finally { setSubmitting(false); }
  }

  async function retry() {
    if (!selected || selected.demo || retrying) return;
    setRetrying(true);
    try {
      const current = await api.retrySigningReview(selected.id);
      setSelected(current);
      setReviews((items) => items.map((item) => item.id === current.id ? current : item));
    } catch (error) { showToast({ message: error.message, tone: "danger" }); }
    finally { setRetrying(false); }
  }

  async function remove() {
    if (!selected || deleting) return;
    setDeleting(true);
    try {
      await api.deleteSigningReview(selected.id);
      setReviews((items) => items.filter((item) => item.id !== selected.id));
      setDeleteOpen(false);
      resetView();
      showToast({ message: "La revisión fue eliminada.", tone: "success" });
    } catch (error) { showToast({ message: error.message, tone: "danger" }); }
    finally { setDeleting(false); }
  }

  if (selected?.status === "ready" && selected.analysis) return (
    <><SigningResult api={api} review={selected} onBack={resetView} onDelete={() => setDeleteOpen(true)} />{deleteOpen && <ConfirmDialog title="¿Eliminar esta revisión?" description="Se borrarán los documentos, el análisis y las conversaciones asociadas. Esta acción no se puede deshacer." confirmLabel="Eliminar" busy={deleting} onClose={() => !deleting && setDeleteOpen(false)} onConfirm={remove} />}</>
  );
  if (selected?.status === "failed") return <SigningError review={selected} retrying={retrying} onRetry={retry} onBack={resetView} />;
  if (selected) return <SigningProcessing review={selected} onBack={resetView} />;

  return (
    <main className="civita-document-page signing-page upload-page">
      <header className="upload-heading">
        <span className="eyebrow">Revisar antes de firmar</span>
        <h1>Entendé qué aceptarías<br />antes de decidir.</h1>
        <p>Subí el documento y sus anexos. Te mostramos sólo lo que puede cambiar tu decisión y qué conviene aclarar.</p>
      </header>

      <div className="upload-layout signing-upload-layout">
        <form className="upload-form signing-upload-form" onSubmit={submit}>
          <div className={`drop-zone ${dragging ? "is-dragging" : ""} ${files.length ? "has-file" : ""}`} onDragEnter={(event) => { event.preventDefault(); setDragging(true); }} onDragOver={(event) => event.preventDefault()} onDragLeave={() => setDragging(false)} onDrop={(event) => { event.preventDefault(); setDragging(false); chooseFiles(event.dataTransfer.files); }}>
            <input ref={fileInputRef} type="file" accept="application/pdf,.pdf,image/jpeg,.jpg,.jpeg,image/png,.png,image/webp,.webp" multiple onChange={(event) => chooseFiles(event.target.files)} />
            {files.length ? (
              <div className="signing-selected-files">
                <header><span><FileCheck2 size={21} /></span><div><strong>{files.length} {files.length === 1 ? "archivo listo" : "archivos listos"}</strong><small>{formatSize(files.reduce((total, file) => total + file.size, 0))} en total</small></div><button type="button" onClick={(event) => { event.stopPropagation(); setFiles([]); if (fileInputRef.current) fileInputRef.current.value = ""; }} aria-label="Quitar archivos"><X size={17} /></button></header>
                <ul>{files.map((file) => <li key={`${file.name}-${file.size}`}><FileText size={14} /><span>{file.name}</span><small>{formatSize(file.size)}</small></li>)}</ul>
              </div>
            ) : <button type="button" onClick={() => fileInputRef.current?.click()}><span className="drop-zone__icon"><UploadCloud size={28} /></span><strong>Subí el documento y sus anexos</strong><small>PDF, JPG, PNG o WEBP · hasta 10 archivos y 20 MB</small></button>}
          </div>

          <label className="question-field"><span>¿Para qué te pidieron firmarlo?</span><textarea value={purpose} onChange={(event) => setPurpose(event.target.value.slice(0, 1_000))} placeholder="Por ejemplo: para contratar un servicio, alquilar, aceptar un presupuesto…" rows={3} required /></label>
          <label className="question-field"><span>¿Qué te prometieron o explicaron? <small>Opcional</small></span><textarea value={promised} onChange={(event) => setPromised(event.target.value.slice(0, 2_000))} placeholder="Contalo con tus palabras. Lo vamos a comparar con el documento." rows={3} /></label>
          <label className="question-field"><span>¿Hay algo que te preocupe? <small>Opcional</small></span><textarea value={concern} onChange={(event) => setConcern(event.target.value.slice(0, 1_500))} placeholder="Por ejemplo: el precio, la renovación, una penalidad o un espacio en blanco." rows={3} /></label>

          <fieldset className="signing-pressure-choice"><legend>¿Cuándo esperan que firmes?</legend><div>{pressureOptions.map((option) => <button className={pressure === option.id ? "is-selected" : ""} type="button" key={option.id} onClick={() => setPressure(option.id)}><span>{option.id === "today" ? <Clock3 size={17} /> : option.id === "soon" ? <CalendarClock size={17} /> : <CheckCircle2 size={17} />}</span><strong>{option.label}</strong><small>{option.description}</small></button>)}</div></fieldset>

          <button className="analyze-button" type="submit" disabled={!files.length || purpose.trim().length < 5 || submitting}>{submitting ? <LoaderCircle className="is-spinning" size={18} /> : <Sparkles size={18} />}{submitting ? "Subiendo documentos…" : "Revisar antes de firmar"}{!submitting && <ArrowRight size={18} />}</button>
          {!authEnabled && <button className="example-button" type="button" onClick={() => selectReview(demoReview)}>Ver un ejemplo completo</button>}
          <p className="privacy-note"><LockKeyhole size={14} /> Tus archivos quedan protegidos en tu espacio y no se comparten con otros usuarios.</p>
        </form>

        <aside className="how-it-works signing-how-it-works">
          <span className="eyebrow">Qué vas a recibir</span>
          <ol>
            <li><span>1</span><div><strong>Hasta tres puntos importantes</strong><small>Sólo lo que puede cambiar tu decisión.</small></div></li>
            <li><span>2</span><div><strong>Costo, duración y salida</strong><small>Los datos prácticos, sin repetir el contrato.</small></div></li>
            <li><span>3</span><div><strong>Hasta tres acciones concretas</strong><small>Preguntas o cambios para pedir por escrito.</small></div></li>
          </ol>
          <div className="scope-note"><Scale size={18} /><p><strong>La decisión sigue siendo tuya.</strong> Civita te ayuda a entender y verificar; no te dice si tenés que firmar.</p></div>
        </aside>
      </div>

      {(loadingHistory || reviews.length > 0) && (
        <section className="recent-documents"><header><div><span className="eyebrow">Tus documentos</span><h2>Revisiones recientes</h2></div></header>{loadingHistory ? <div className="history-loading"><LoaderCircle className="is-spinning" size={18} /> Cargando…</div> : <div className="document-list">{reviews.map((review) => <button type="button" key={review.id} onClick={() => selectReview(review)}><span className="document-list__icon"><FileCheck2 size={19} /></span><span><strong>{review.analysis?.title || review.sources?.[0]?.fileName || "Revisión en curso"}</strong><small>{review.sources?.length || 0} {(review.sources?.length || 0) === 1 ? "archivo" : "archivos"} · {formatDate(review.createdAt)}</small></span><span className={`document-status is-${review.status}`}>{review.status === "ready" ? "Lista" : review.status === "failed" ? "Revisar" : "Procesando"}</span><ChevronRight size={17} /></button>)}</div>}</section>
      )}
    </main>
  );
}
